import { createTheme } from "@mui/material/styles";
import { createBreakpoints, createStyled } from "@mui/system";

const titleFont = "Proxima Nova, sans-serif";
const primaryFont = "Proxima Nova, sans-serif";

declare module "@mui/material/Button" {
  interface ButtonPropsSizeOverrides {
    tiny: true;
  }
}

const colors = {
  magenta: "#FF0D65",
  navy: "#112e7f",
  grey: "#e0e0e0",
  paper: "#f2f2fc",
  steel: "#7d8dba",
  negative: "#d50001",
  positive: "#2dcc6f",
  info: "#2497f3",
  panel: "#fafaff",
  neutral: "#e18713",
  warn: "#fff3e1",
  snuff: "#D5D4E8",
  greyBackground: "#a7adc2",
  cardBackground: "#EBEBEB",
  white: "#fff"
};

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1600,
  },
});

const theme = createTheme({
  colors,
  palette: {
    primary: {
      main: colors.navy,
    },
    secondary: {
      main: colors.magenta,
    },
    info: {
      main: "#2497f3",
      background: "#e9f4fd",
    },
    positive: {
      main: colors.positive,
      800: "#166637",
    },
    negative: {
      main: colors.negative,
      800: "#6a0000",
    },
    error: {
      main: colors.negative,
    },
  },
  typography: {
    fontSize: 14,
    fontFamily: [primaryFont].join(","),
    primaryFont,
    titleFont,
    h1: {
      fontSize: "1.5em",
      fontWeight: 600,
      fontFamily: titleFont,
      color: colors.navy,
      margin: "8px 0",
    },
    h2: {
      fontSize: "1.5em",
      fontFamily: titleFont,
      color: colors.navy,
      margin: "8px 0",
    },
    h3: {
      fontSize: "1.2em",
      fontWeight: "bold",
      fontFamily: titleFont,
      color: colors.navy,
    },
    h4: {
      fontSize: "1em",
      fontFamily: titleFont,
    },
    subLabel: {
      display: "block",
      fontSize: "0.75em",
      fontFamily: titleFont,
      color: colors.navy,
    },
    subtitle2: {
      fontSize: "0.75em",
      fontFamily: titleFont,
      color: colors.navy,
      marginBottom: "0.5em",
    },
  },
  breakpoints,
  components: {
    MuiStepper: {
      styleOverrides: {
        root: {
          paddingTop: "12px",
          paddingBottom: "24px",
          [breakpoints.down("md")]: {
            paddingTop: "0px",
          },
          [breakpoints.down("sm")]: {
            paddingBottom: "12px",
          },
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          cursor: "pointer",
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            marginTop: "8px!important",
            fontSize: "0.75rem",
          },
        }),
      },
    },
    MuiIconButton: {
      variants: [
        {
          props: { size: "small" },
          style: {
            padding: "2px",
          },
        },
        {
          props: { size: "small" },
          style: {
            padding: "1px",
          },
        },
      ],
    },
    MuiSvgIcon: {
      variants: [
        {
          props: { fontSize: "small" },
          style: {
            width: "18px",
            height: "18px",
          },
        },
        {
          props: { fontSize: "small" },
          style: {
            width: "14px",
            height: "14px",
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none!important",
          textTransform: "none",
          minWidth: "100px",
        },
      },
      variants: [
        {
          props: { size: "huge" },
          style: {
            padding: "12px 16px",
          },
        },
        {
          props: { variant: "contained", color: "grey" },
          style: {
            backgroundColor: colors.grey,
          },
        },
        {
          props: { size: "tiny" },
          style: {
            fontSize: "14px !important",
            minHeight: "unset !important",
          },
        },
      ],
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: [theme.palette.primary.main],
        }),
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          "&[hidden]": {
            display: "none",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          textAlign: "center",
          margin: "1em 0 0",
          fontSize: "1.2em",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down("sm")]: {
            fontSize: "1.2rem",
            lineHeight: "1.2rem",
          },
          "&>.MuiTypography-root": {
            fontWeight: 600,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginRight: 0,
        },
      },
    },
  },
});

export type AppColors = typeof colors;
export default theme;

export const appStyled = createStyled({ defaultTheme: theme });
